import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SettingsPowerIcon from "@material-ui/icons/SettingsPower";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactSourceModal from "../../components/ContactSourceModal";
import ContactSourceContactsModal from "../../components/ContactSourceContactsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Utilitários para fontes de contato
import { getSourceIcon, getSourceColor, getSourceIconColor, isAdminSource } from "../../utils/contactSourceUtils";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

// Componente Badge para status
const ContactSourceStatusBadge = ({ status }) => {
  const color = status === "ativo" ? "#43a047" : "#f44336";
  const label = status === "ativo" ? "Ativo" : "Inativo";
  
  return (
    <span style={{
      display: "inline-block",
      minWidth: 70,
      padding: "2px 10px",
      borderRadius: 12,
      background: color,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      textAlign: "center"
    }}>{label}</span>
  );
};

const ContactSources = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [contactSources, setContactSources] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [contactSourceModalOpen, setContactSourceModalOpen] = useState(false);
  const [selectedContactSource, setSelectedContactSource] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingContactSource, setDeletingContactSource] = useState(null);
  const [contactSourceContactsModalOpen, setContactSourceContactsModalOpen] = useState(false);
  const [selectedContactSourceForContacts, setSelectedContactSourceForContacts] = useState(null);

  useEffect(() => {
    fetchContactSources();
  }, []);


  const fetchContactSources = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contact-sources");
      // A API retorna { records, count, hasMore }, mas precisamos apenas do array records
      const contactSourcesList = data.records || data || [];
      setContactSources(Array.isArray(contactSourcesList) ? contactSourcesList : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleToggleContactSourceStatus = async (contactSource) => {
    try {
      const newStatus = contactSource.isActive ? false : true;
      
      // Se está tentando desativar, verificar se não é a última fonte ativa
      if (!newStatus) {
        const activeSources = contactSources.filter(source => source.isActive);
        if (activeSources.length <= 1) {
          toast.warning("Não é possível desativar todas as fontes de contato. Pelo menos uma fonte deve estar ativa.");
          return;
        }
      }
      
      await api.put(`/contact-sources/${contactSource.id}`, {
        isActive: newStatus
      });
      
      // Atualiza a lista após a mudança
      fetchContactSources();
      
      toast.success(
        newStatus
          ? "Fonte de contato ativada com sucesso!"
          : "Fonte de contato desativada com sucesso!"
      );
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteContactSource = async (contactSourceId) => {
    try {
      await api.delete(`/contact-sources/${contactSourceId}`);
      toast.success("Fonte de contato excluída com sucesso!");
      fetchContactSources();
      setConfirmModalOpen(false);
      setDeletingContactSource(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteClick = (contactSource) => {
    if (contactSource.contactCount > 0) {
      toast.warning("Não é possível excluir uma fonte de contato que possui contatos associados.");
      return;
    }
    
    setConfirmModalOpen(true);
    setDeletingContactSource(contactSource);
  };

  const handleOpenContactSourceModal = () => {
    setSelectedContactSource(null);
    setContactSourceModalOpen(true);
  };

  const handleCloseContactSourceModal = () => {
    setSelectedContactSource(null);
    setContactSourceModalOpen(false);
  };

  const handleEditContactSource = (contactSource) => {
    setSelectedContactSource(contactSource);
    setContactSourceModalOpen(true);
  };

  const handleOpenContactSourceContactsModal = (contactSource) => {
    setSelectedContactSourceForContacts(contactSource);
    setContactSourceContactsModalOpen(true);
  };

  const handleCloseContactSourceContactsModal = () => {
    setContactSourceContactsModalOpen(false);
    setSelectedContactSourceForContacts(null);
  };

  const filteredContactSources = Array.isArray(contactSources) 
    ? contactSources.filter(source =>
        source.name.toLowerCase().includes(searchParam) ||
        source.description.toLowerCase().includes(searchParam)
      )
    : [];

  // Verificar permissão do usuário
  if (user.profile === "user" && user.allowContactSources === "disabled") {
    return (
      <MainContainer>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <h3>Acesso Negado</h3>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContactSourceModal
        open={contactSourceModalOpen}
        onClose={handleCloseContactSourceModal}
        contactSourceId={selectedContactSource?.id}
        onSave={fetchContactSources}
      />
      
      <ConfirmationModal
        title={`${i18n.t("contactSources.confirmationModal.deleteTitle")} ${deletingContactSource?.name}?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteContactSource(deletingContactSource?.id)}
      >
        {i18n.t("contactSources.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ContactSourceContactsModal
        open={contactSourceContactsModalOpen}
        onClose={handleCloseContactSourceContactsModal}
        contactSource={selectedContactSourceForContacts}
      />
      
      <MainHeader>
        <Title>{i18n.t("contactSources.title")} ({filteredContactSources.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contactSources.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactSourceModal}
          >
            {i18n.t("contactSources.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {i18n.t("contactSources.table.name")}
              </TableCell>
              <TableCell>
                {i18n.t("contactSources.table.description")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactSources.table.contactCount")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactSources.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactSources.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredContactSources.map((contactSource) => (
              <TableRow key={contactSource.id}>
                <TableCell>
                  <span style={{ 
                    fontWeight: contactSource.isFixed ? "bold" : "normal",
                    color: contactSource.isFixed ? theme.palette.primary.main : "inherit",
                    display: "flex",
                    alignItems: "center"
                  }}>
                    {contactSource.isFixed && getSourceIcon(contactSource.name) && (() => {
                      const sourceInfo = getSourceIcon(contactSource.name);
                      const IconComponent = sourceInfo.icon;
                      
                      return (
                        <span style={{ marginRight: "8px" }}>
                          <IconComponent 
                            style={{ 
                              fontSize: "18px", 
                              color: isAdminSource(contactSource.name) ? "#000000" : getSourceIconColor(contactSource.name)
                            }} 
                          />
                        </span>
                      );
                    })()}
                    {contactSource.name}
                  </span>
                </TableCell>
                <TableCell>
                  {contactSource.description}
                </TableCell>
                <TableCell align="center">
                  {contactSource.contactCount > 0 ? (
                    <span 
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 500,
                        fontSize: "14px"
                      }}
                      onClick={() => handleOpenContactSourceContactsModal(contactSource)}
                    >
                      {contactSource.contactCount}
                    </span>
                  ) : (
                    <span style={{ color: "#999", fontSize: "14px" }}>
                      {contactSource.contactCount || 0}
                    </span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <ContactSourceStatusBadge status={contactSource.isActive ? "ativo" : "inativo"} />
                </TableCell>
                <TableCell align="center">
                  {!contactSource.isFixed && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEditContactSource(contactSource)}
                      >
                        <EditIcon style={{ color: theme.palette.primary.main }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(contactSource)}
                      >
                        <DeleteOutlineIcon style={{ color: theme.palette.primary.main }} />
                      </IconButton>
                    </>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleToggleContactSourceStatus(contactSource)}
                    title={contactSource.isActive ? "Inativar fonte" : "Ativar fonte"}
                    color={contactSource.isActive ? "secondary" : "primary"}
                  >
                    <SettingsPowerIcon style={{ color: theme.palette.primary.main }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactSources; 